/**
 * Cart Persistence on Logout/Login
 * Saves cart items to localStorage on logout and restores on login
 */

class CartPersistence {
  constructor() {
    this.STORAGE_PREFIX = 'cart_';
    this.RESTORE_FLAG = 'cart_restore_pending';
    this.LOGGED_IN_FLAG = 'cart_was_logged_in';
    this.RESTORE_DONE_FLAG = 'cart_restore_done';
    this.enabled = true;
    this.init();
  }

  /**
   * Check if localStorage is available
   */
  isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize cart persistence handlers
   */
  init() {
    // Check if feature is disabled via window variable
    if (window.DISABLE_CART_PERSISTENCE === true) {
      this.enabled = false;
      return;
    }

    // Check if localStorage is available
    if (!this.isLocalStorageAvailable()) {
      this.enabled = false;
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupHandlers());
    } else {
      this.setupHandlers();
    }
  }

  /**
   * Setup logout and login handlers
   */
  async setupHandlers() {
    // Check if user logged out and clear cart
    await this.handleLogoutDetection();
    
    // Handle login restoration BEFORE auto-save to avoid overwriting saved cart
    await this.handleLoginRestore();
    
    // Auto-save cart if customer is logged in (after restoration)
    await this.autoSaveCart();
  }

  /**
   * Get customer ID from Shopify's global objects
   */
  getCustomerId() {
    // Try multiple sources for customer ID
    try {
      // Check ShopifyAnalytics
      if (window.ShopifyAnalytics?.meta?.page?.customerId) {
        return window.ShopifyAnalytics.meta.page.customerId.toString();
      }
      
      // Check if customer object is available (set in theme.liquid)
      if (window.customerInfo?.id) {
        return window.customerInfo.id.toString();
      }
    } catch (error) {
      // Silent fail
    }
    
    return null;
  }

  /**
   * Get current cart from Shopify
   */
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Save cart to localStorage
   */
  saveCart(customerId, cartData) {
    try {
      if (!customerId || !cartData) return false;
      
      // Extract only necessary data: variant_id and quantity
      const cartItems = cartData.items.map(item => ({
        variant_id: item.variant_id,
        quantity: item.quantity
      }));
      
      const storageKey = `${this.STORAGE_PREFIX}${customerId}`;
      localStorage.setItem(storageKey, JSON.stringify(cartItems));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Load cart from localStorage
   */
  loadCart(customerId) {
    try {
      if (!customerId) return null;
      
      const storageKey = `${this.STORAGE_PREFIX}${customerId}`;
      const cartData = localStorage.getItem(storageKey);
      
      if (cartData) {
        return JSON.parse(cartData);
      }
    } catch (error) {
      // Silent fail
    }
    
    return null;
  }

  /**
   * Clear saved cart from localStorage
   */
  clearSavedCart(customerId) {
    try {
      if (!customerId) return;
      
      const storageKey = `${this.STORAGE_PREFIX}${customerId}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Clear current Shopify cart
   */
  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) throw new Error('Failed to clear cart');
      
      // Wait for cart to be cleared and update UI
      const cart = await this.waitForCartUpdate(0);
      if (cart) {
        await this.updateCartUI(cart);
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Wait for cart to reach expected state by polling
   */
  async waitForCartUpdate(expectedCount, maxWaitMs = 3000) {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      try {
        const response = await fetch('/cart.js', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        if (response.ok) {
          const cart = await response.json();
          if (cart.item_count === expectedCount) {
            return cart; // Cart updated successfully
          }
        }
      } catch (error) {
        // Continue polling on error
      }

      // Check every 100ms
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Timeout - return whatever we got
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Force cart UI to update with cart data
   */
  async updateCartUI(cart) {
    try {
      if (!cart) return;
      
      const cartIcon = document.querySelector('#cart-icon-bubble');
      let cartBubble = document.querySelector('.cart-count-bubble');
      
      if (cart.item_count === 0) {
        // Hide bubble when cart is empty
        if (cartBubble) {
          cartBubble.style.display = 'none';
        }
      } else {
        // Show bubble and update count
        if (cartBubble) {
          // Bubble exists - just update it
          cartBubble.style.display = '';
          const countSpan = cartBubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) {
            countSpan.textContent = cart.item_count;
          }
          const visuallyHiddenSpan = cartBubble.querySelector('.visually-hidden');
          if (visuallyHiddenSpan) {
            visuallyHiddenSpan.textContent = visuallyHiddenSpan.textContent.replace(/\d+/, cart.item_count);
          }
        } else if (cartIcon) {
          // Bubble doesn't exist - create it
          cartBubble = document.createElement('div');
          cartBubble.className = 'cart-count-bubble';
          cartBubble.innerHTML = `
            <span aria-hidden="true">${cart.item_count}</span>
            <span class="visually-hidden">${cart.item_count} items</span>
          `;
          cartIcon.appendChild(cartBubble);
        }
      }

      // Trigger cart drawer refresh if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.renderContents();
        } catch (e) {
          // Silent fail if render fails
        }
      }

      // Publish cart update event
      if (window.PubSub && typeof window.PubSub.publish === 'function') {
        try {
          window.PubSub.publish('cart-update');
        } catch (e) {
          // Silent fail
        }
      }

      // Dispatch custom event for other cart listeners
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (e) {
        // Silent fail
      }
    } catch (error) {
      // Silent fail - cart data is still correct, just UI might not update
    }
  }

  /**
   * Restore cart items from localStorage
   */
  async restoreCart(cartItems) {
    try {
      if (!cartItems || cartItems.length === 0) return false;
      
      // First, clear the current cart
      await this.clearCart();
      
      // Add items back to cart
      const formData = {
        items: cartItems
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        // Try adding items one by one if batch add fails
        return await this.restoreCartIndividually(cartItems);
      }
      
      // Wait for cart to be updated and update UI
      // Calculate expected total quantity (not just line item count)
      const expectedCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      const cart = await this.waitForCartUpdate(expectedCount);
      if (cart) {
        await this.updateCartUI(cart);
      }
      
      return true;
    } catch (error) {
      // Try individual item restoration as fallback
      return await this.restoreCartIndividually(cartItems);
    }
  }

  /**
   * Restore cart items one by one (fallback method)
   */
  async restoreCartIndividually(cartItems) {
    let successCount = 0;
    
    for (const item of cartItems) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: item.variant_id,
            quantity: item.quantity
          })
        });
        
        if (response.ok) {
          successCount++;
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    // Wait for cart to be updated and update UI if any items were restored
    if (successCount > 0) {
      // Calculate expected total quantity from successfully added items
      const expectedCount = cartItems.slice(0, successCount).reduce((sum, item) => sum + item.quantity, 0);
      const cart = await this.waitForCartUpdate(expectedCount);
      if (cart) {
        await this.updateCartUI(cart);
      }
    }
    
    return successCount > 0;
  }

  /**
   * Detect if user just logged out and clear their cart
   */
  async handleLogoutDetection() {
    if (!this.enabled) return;

    const customerId = this.getCustomerId();
    const wasLoggedIn = localStorage.getItem(this.LOGGED_IN_FLAG);

    // If was logged in but now not logged in, user has logged out
    if (wasLoggedIn && !customerId) {
      try {
        // Before clearing, make sure the cart was saved (it should have been auto-saved)
        // Get the logged-in customer ID from a separate localStorage key
        const lastCustomerId = localStorage.getItem(this.LOGGED_IN_FLAG + '_id');

        if (lastCustomerId) {
          // Cart should already be saved from autoSaveCart, but verify it exists
          const savedCart = this.loadCart(lastCustomerId);
          if (savedCart && savedCart.length > 0) {
            // Set a persistent flag that restoration is needed on next login
            localStorage.setItem(this.RESTORE_FLAG, lastCustomerId);
          }
        }

        await this.clearCart();
      } catch (error) {
        // Silent fail
      }

      // Remove login flag and customer ID
      localStorage.removeItem(this.LOGGED_IN_FLAG);
      localStorage.removeItem(this.LOGGED_IN_FLAG + '_id');
      sessionStorage.removeItem(this.RESTORE_DONE_FLAG);
    }
  }

  /**
   * Auto-save cart if customer is logged in
   * This runs on every page load to ensure cart is always backed up
   */
  async autoSaveCart() {
    if (!this.enabled) return;

    const customerId = this.getCustomerId();

    if (!customerId) {
      return;
    }

    // Check if we just logged in - don't auto-save/clear on first login page load
    const urlParams = new URLSearchParams(window.location.search);
    const customerPosted = urlParams.get('customer_posted') === 'true';

    if (customerPosted) {
      return;
    }

    // Set flag that user is logged in and store customer ID for logout detection
    localStorage.setItem(this.LOGGED_IN_FLAG, 'true');
    localStorage.setItem(this.LOGGED_IN_FLAG + '_id', customerId);

    try {
      const cart = await this.getCurrentCart();

      if (cart && cart.items && cart.items.length > 0) {
        // Cart has items - save it to localStorage
        this.saveCart(customerId, cart);

        // Mark restoration as done since we have items (user is actively using cart)
        sessionStorage.setItem(this.RESTORE_DONE_FLAG, 'true');
      } else {
        // Cart is empty - only clear saved cart if there's no restore pending
        // This prevents clearing the cart that's waiting to be restored after login
        const restorePending = localStorage.getItem(this.RESTORE_FLAG);
        if (!restorePending) {
          this.clearSavedCart(customerId);
        }
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Handle login restoration
   */
  async handleLoginRestore() {
    if (!this.enabled) return;

    const customerId = this.getCustomerId();

    if (!customerId) {
      return;
    }

    // Check if user just logged in
    const urlParams = new URLSearchParams(window.location.search);
    const customerPosted = urlParams.get('customer_posted') === 'true';

    // Check for persistent restore flag in localStorage (set during logout)
    const restorePendingId = localStorage.getItem(this.RESTORE_FLAG);

    // Restore if:
    // 1. User just logged in (customer_posted=true), OR
    // 2. There's a restore pending flag AND it matches this customer
    if (customerPosted || (restorePendingId && restorePendingId === customerId)) {

      // Load saved cart
      const savedCart = this.loadCart(customerId);

      if (savedCart && savedCart.length > 0) {
        // Restore cart and wait for completion
        const success = await this.restoreCart(savedCart);

        if (success) {
          // Fetch the restored cart and save it back to localStorage
          // This ensures the cart is preserved for future page loads
          const cart = await this.getCurrentCart();
          if (cart && cart.items && cart.items.length > 0) {
            this.saveCart(customerId, cart);
          }

          // Clear the restore pending flag
          localStorage.removeItem(this.RESTORE_FLAG);

          // Mark restoration as done for this session
          sessionStorage.setItem(this.RESTORE_DONE_FLAG, 'true');
        }
      } else {
        // No saved cart found, clear the restore flag
        localStorage.removeItem(this.RESTORE_FLAG);
      }
    }
  }

}

// Initialize cart persistence when script loads
// To disable: add window.DISABLE_CART_PERSISTENCE = true; before this script loads
const cartPersistence = new CartPersistence();

// Expose for debugging in browser console
window.cartPersistence = cartPersistence;
